import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui-kit";
import { track } from "@/lib/track";
import { drawMilestone, canvasToBlob } from "@/lib/milestoneCanvas";
import { X, Download, Share2, Copy, Check } from "lucide-react";

// Renders a shareable milestone card on a canvas with aspect toggle,
// download, Web Share, and a copyable public link.
export default function MilestoneShareModal({ card, onClose }) {
  const canvasRef = useRef(null);
  const [ratio, setRatio] = useState("square");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (canvasRef.current && card) drawMilestone(canvasRef.current, card, ratio);
  }, [card, ratio]);

  const fileName = `numbersense-${(card?.first_name || "milestone").toLowerCase()}-${ratio}.png`;

  const download = async () => {
    const blob = await canvasToBlob(canvasRef.current);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = fileName; a.click();
    URL.revokeObjectURL(url);
    track("milestone_downloaded", { kind: card.kind, ratio });
  };

  const share = async () => {
    const blob = await canvasToBlob(canvasRef.current);
    const file = new File([blob], fileName, { type: "image/png" });
    const text = `${card.first_name} just mastered a new math skill on NumberSense! ${card.share_url}`;
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: card.title, text });
        track("milestone_shared", { kind: card.kind, ratio, method: "web_share" });
      } else if (navigator.share) {
        await navigator.share({ title: card.title, text, url: card.share_url });
        track("milestone_shared", { kind: card.kind, ratio, method: "link_share" });
      } else {
        await download();
      }
    } catch { /* user cancelled */ }
  };

  const copyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(card.share_url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = card.share_url;
        ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.focus(); ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      track("milestone_link_copied", { kind: card.kind });
    } catch { toast.error("Couldn't copy the link."); }
  };

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center bg-black/50 p-4 overflow-y-auto" onClick={onClose} data-testid="milestone-share-modal">
      <div className="w-full max-w-md bg-[var(--ns-surface)] rounded-[var(--ns-radius)] border border-[var(--ns-border)] p-5 ns-rise my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-head text-lg font-bold">Share this win</h3>
          <button onClick={onClose} data-testid="milestone-close" className="text-[var(--ns-subtle)] hover:text-[var(--ns-text)]"><X size={20} /></button>
        </div>

        <div className="rounded-[var(--ns-radius)] overflow-hidden border border-[var(--ns-border)] bg-[var(--ns-surface-subtle)] grid place-items-center p-3">
          <canvas ref={canvasRef} data-testid="milestone-canvas"
            className="w-full h-auto rounded-lg shadow-sm" style={{ maxHeight: "46vh", objectFit: "contain" }} />
        </div>

        <div className="mt-3 flex rounded-full bg-[var(--ns-surface-subtle)] p-1">
          {[["square", "Square"], ["portrait", "Portrait"]].map(([v, l]) => (
            <button key={v} data-testid={`ratio-${v}`} onClick={() => setRatio(v)}
              className="flex-1 rounded-full py-2 text-sm font-semibold transition-colors"
              style={{ background: ratio === v ? "var(--ns-surface)" : "transparent", color: ratio === v ? "var(--ns-primary)" : "var(--ns-muted)" }}>
              {l}
            </button>
          ))}
        </div>

        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button variant="outline" data-testid="milestone-download" onClick={download}><Download size={16} /> Download</Button>
          <Button data-testid="milestone-share-btn" onClick={share}><Share2 size={16} /> Share</Button>
        </div>

        <button onClick={copyLink} data-testid="milestone-copy-link"
          className="mt-3 w-full flex items-center justify-center gap-2 text-sm font-semibold text-[var(--ns-primary)] py-2">
          {copied ? <><Check size={15} /> Link copied</> : <><Copy size={15} /> Copy share link</>}
        </button>
        <p className="text-xs text-center text-[var(--ns-subtle)]">Shared cards never show last names or personal details.</p>
      </div>
    </div>
  );
}
